/* ===========================================================================
   Wasser-Sparrechner
   ---------------------------------------------------------------------------
   Macht die serverseitig gerenderte Rechnerkarte interaktiv. Es wird kein
   Markup erzeugt, nur Text in vorhandenen [data-calc-out]-Feldern ersetzt.
   Formeln und Zahlenformat entsprechen sections/savings-calculator.liquid und
   snippets/formatted-number.liquid – Aenderungen immer an beiden Stellen.
   Kein Tracking, keine Speicherung, keine externen Abhaengigkeiten.
   =========================================================================== */

(function () {
  'use strict';

  var TAGE_PRO_JAHR = 365;
  var KG_PRO_LITER = 1;
  var NBSP = '\u00A0';

  /* Rundung wie Liquid: halbe Stellen von null weg. */
  function roundHalfAway(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
  }

  /* Deutsches Zahlenformat – Gegenstueck zu snippets/formatted-number.liquid. */
  function formatNumber(value, decimals, trim) {
    decimals = decimals || 0;
    var factor = Math.pow(10, decimals);
    var scaled = roundHalfAway(value * factor);
    var sign = '';
    if (scaled < 0) {
      sign = '−';
      scaled = -scaled;
    }
    var integer = String(Math.floor(scaled / factor)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var fraction = '';
    if (decimals > 0) {
      fraction = String(scaled % factor);
      while (fraction.length < decimals) fraction = '0' + fraction;
      if (trim) fraction = fraction.replace(/0+$/, '');
    }
    return sign + integer + (fraction ? ',' + fraction : '');
  }

  function plural(count, one, many) {
    return count === 1 ? one : many;
  }

  /* Reine Berechnung – ohne DOM. Gerechnet wird mit ganzen Zahlen
     (Milliliter, tausendstel Euro), damit Rundungen an halben Werten exakt
     sind und mit Liquid uebereinstimmen. */
  function calculate(v, bottleSize, packSize) {
    var persons = Math.round(v.persons);
    var years = Math.round(v.years);
    var milliliters = persons * Math.round(v.liters * 1000) * TAGE_PRO_JAHR * years;
    var cost = roundHalfAway((milliliters * Math.round(v.price * 1000)) / 1000000);
    var purchase = roundHalfAway(v.system);
    var bottleMl = Math.max(1, Math.round(bottleSize * 1000));
    var bottles = Math.ceil(milliliters / bottleMl);
    return {
      persons: persons,
      years: years,
      liters: milliliters / 1000,
      cost: cost,
      purchase: purchase,
      difference: cost - purchase,
      weightKg: roundHalfAway((milliliters / 1000) * KG_PRO_LITER),
      weightTenthTonnes: roundHalfAway((milliliters * KG_PRO_LITER) / 100000),
      bottles: bottles,
      packs: Math.ceil(bottles / packSize)
    };
  }

  function setup(root) {
    if (root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    var currency = root.dataset.currency || '€';
    var bottleSize = parseFloat(root.dataset.bottle) > 0 ? parseFloat(root.dataset.bottle) : 1.5;
    var packSize = parseInt(root.dataset.pack, 10) > 0 ? parseInt(root.dataset.pack, 10) : 6;

    var inputs = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-calc-input]'), function (input) {
      inputs[input.dataset.calcInput] = input;
    });
    if (!inputs.persons || !inputs.liters || !inputs.price || !inputs.system || !inputs.years) return;

    var outputs = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-calc-out]'), function (node) {
      (outputs[node.dataset.calcOut] = outputs[node.dataset.calcOut] || []).push(node);
    });
    var diffBox = root.querySelector('[data-calc-diff]');
    var live = root.querySelector('[data-calc-live]');
    var liveTimer = null;

    function euro(value) {
      return formatNumber(value, 0) + NBSP + currency;
    }

    function set(key, text) {
      (outputs[key] || []).forEach(function (node) {
        if (node.textContent !== text) node.textContent = text;
      });
    }

    function read(name) {
      var value = parseFloat(inputs[name].value);
      return isFinite(value) ? value : 0;
    }

    function paintFill(input) {
      var min = parseFloat(input.min);
      var max = parseFloat(input.max);
      var value = parseFloat(input.value);
      var percent = max > min ? ((value - min) / (max - min)) * 100 : 0;
      input.style.setProperty('--wq-fill', Math.min(100, Math.max(0, percent)).toFixed(2) + '%');
    }

    function update(announce) {
      var v = {
        persons: read('persons'),
        liters: read('liters'),
        price: read('price'),
        system: read('system'),
        years: read('years')
      };
      var r = calculate(v, bottleSize, packSize);

      var texts = {
        persons: formatNumber(r.persons, 0) + ' ' + plural(r.persons, 'Person', 'Personen'),
        liters: formatNumber(v.liters, 2, true) + ' Liter pro Tag',
        price: formatNumber(v.price, 2) + NBSP + currency + ' pro Liter',
        system: euro(v.system),
        years: formatNumber(r.years, 0) + ' ' + plural(r.years, 'Jahr', 'Jahre')
      };

      Object.keys(texts).forEach(function (name) {
        set(name, texts[name]);
        inputs[name].setAttribute('aria-valuetext', texts[name].replace(/\u00A0/g, ' '));
        paintFill(inputs[name]);
      });

      var tonnes = formatNumber(r.weightTenthTonnes / 10, 1, true);
      var weight = r.liters >= 1000
        ? tonnes + ' ' + (tonnes === '1' ? 'Tonne' : 'Tonnen')
        : formatNumber(r.weightKg, 0) + ' Kilogramm';

      set('years_result', texts.years);
      var yearsIn = 'in ' + formatNumber(r.years, 0) + ' ' + plural(r.years, 'Jahr', 'Jahren');
      set('years_in', yearsIn);
      set('cost', euro(r.cost));
      set('diff', euro(r.difference));
      set('liters_total_text', formatNumber(r.liters, 0) + ' Liter');
      set('weight', weight);
      set('bottles', formatNumber(r.bottles, 0) + ' ' + plural(r.bottles, 'Flasche', 'Flaschen'));
      set('bottles_caption', 'à ' + formatNumber(bottleSize, 2, true) + ' Liter, rund ' + formatNumber(r.packs, 0) + ' Gebinde mit je ' + packSize + ' Flaschen');
      set('formula_liters', formatNumber(r.persons, 0) + ' × ' + formatNumber(v.liters, 2, true) + ' × 365 × ' + formatNumber(r.years, 0) + ' = ' + formatNumber(r.liters, 2, true) + ' Liter');
      set('formula_cost', formatNumber(r.liters, 2, true) + ' × ' + formatNumber(v.price, 2) + NBSP + currency + ' = ' + euro(r.cost));
      set('formula_diff', euro(r.cost) + ' − ' + euro(r.purchase) + ' = ' + euro(r.difference));

      if (diffBox) diffBox.dataset.state = r.difference < 0 ? 'negative' : 'positive';

      if (announce && live) {
        window.clearTimeout(liveTimer);
        liveTimer = window.setTimeout(function () {
          var diffLabel = r.difference < 0 ? 'Rechnerische Differenz' : 'Mögliches Einsparpotenzial, geschätzt';
          live.textContent = 'Kosten für Flaschenwasser ' + yearsIn + ': ' + euro(r.cost).replace(/\u00A0/g, ' ') + '. ' + diffLabel + ': ' + euro(r.difference).replace(/\u00A0/g, ' ') + '.';
        }, 700);
      }
    }

    Object.keys(inputs).forEach(function (name) {
      inputs[name].addEventListener('input', function () {
        update(true);
      });
    });

    update(false);
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-savings-calculator]'), function (root) {
      try {
        setup(root);
      } catch (error) {
        /* Rechner bleibt mit den serverseitigen Startwerten stehen. */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
